// ToDo: WW-7459 Move to shared shadow folder
import styles from '../../../../../../erp/components/tab-panels/positions/components/position-mask/PositionMask.module.scss';

import { ArrowBack, Close, Delete } from '@mui/icons-material';
import { Grid, IconButton, Theme, useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EndAdornment } from '@work4all/components/lib/input/labeled-input/components/end-adornment/EndAdornment';
import { NumberInput } from '@work4all/components/lib/input/number-input';

import { ServiceContractPosition } from '@work4all/models/lib/Classes/ServiceContractPosition.entity';
import { ServiceContractPositionKind } from '@work4all/models/lib/Enums/ServiceContractPositionKind.enum';

import { LabeledInput } from '../../../../../../../../locked-inputs';
import { Collapse } from '../../../../../../../components';
import { LockOverride } from '../../../../../../../components/LockOverride';
import { useEditTable } from '../edit-table/components/edit-table-provider/EditTableProvider';
import { EditTableEntry, EditTableMask } from '../edit-table/types';

export type IPositionMaskProps = EditTableMask<
  ServiceContractPosition & EditTableEntry
>;

export function PositionMask(props: IPositionMaskProps) {
  const { item: position, onCloseClick, editableCols = [] } = props;

  const { onRemoveComponent, onEditComponent } = useEditTable();

  const disabled = position?.cacheOnly;
  const [resultedPosition, setResultedPosition] = useState(position);

  useEffect(() => {
    setResultedPosition(position);
  }, [position]);

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );
  const { t } = useTranslation();

  const onChange = useCallback(
    (props: { id: string; value: number | string | boolean }) => {
      const { id, value } = props;
      const newPosition = resultedPosition;

      setResultedPosition({ ...newPosition, [id]: value });
    },
    [resultedPosition]
  );

  const onBlur = useCallback(
    (props: { id: string; value: number | string | boolean }) => {
      const { id, value } = props;

      onEditComponent({
        position: {
          id: position.id,
          [id]: value,
        },
      });
    },
    [onEditComponent, position]
  );

  if (!resultedPosition) {
    return null;
  }

  return (
    <LockOverride forceLock={disabled}>
      <div className={styles.outerWrap}>
        <div className={styles.header}>
          <IconButton onClick={onCloseClick}>
            {isMobile ? <ArrowBack /> : <Close />}
          </IconButton>
          <div className={styles.titleContainer}>{t('COMMON.POSITION')}</div>
          <IconButton
            color="primary"
            onClick={(e) => {
              onRemoveComponent([position?.id]);
              onCloseClick(e);
            }}
          >
            <Delete />
          </IconButton>
        </div>
        <div className={styles.scrollContainer}>
          <Collapse
            defaultOpen={true}
            title={t('COMMON.DESCRIPTION')}
            className={styles.topSection}
          >
            <LabeledInput
              value={resultedPosition?.longtext}
              multiline={true}
              style={{ minHeight: '12rem' }}
              disabled={!editableCols.includes('longtext')}
              onBlur={(e) => {
                onBlur({ id: 'longtext', value: e.target.value });
              }}
              onChange={(e) => {
                onChange({ id: 'longtext', value: e.target.value });
              }}
              label={t('FIELDS.longtext')}
            />
          </Collapse>
          {![
            ServiceContractPositionKind.UEBERSCHRIFT,
            ServiceContractPositionKind.TEXTZEILE,
          ].includes(position?.positionKind) && (
            <Collapse
              defaultOpen={true}
              title={`${t('COMMON.ERP.CALCULATION')} / ${t(
                'COMMON.ERP.PRICING'
              )}`}
            >
              <Grid container spacing="1rem">
                <Grid item xs={4}>
                  <LabeledInput
                    value={resultedPosition?.amountDecimal}
                    type="number"
                    disabled={!editableCols.includes('amountDecimal')}
                    onBlur={(e) => {
                      onBlur({
                        id: 'amountDecimal',
                        value: parseFloat(e.target.value),
                      });
                    }}
                    onChange={(e) => {
                      onChange({
                        id: 'amountDecimal',
                        value: parseFloat(e.target.value),
                      });
                    }}
                    label={t('FIELDS.amount')}
                    selectOnFocus
                  />
                </Grid>
                <Grid item xs={4}>
                  <LabeledInput
                    value={resultedPosition?.discount}
                    disabled={!editableCols.includes('discount')}
                    type="number"
                    onBlur={(e) => {
                      onBlur({
                        id: 'discount',
                        value: parseFloat(e.target.value),
                      });
                    }}
                    onChange={(e) => {
                      onChange({
                        id: 'discount',
                        value: parseFloat(e.target.value),
                      });
                    }}
                    label={t('FIELDS.discount')}
                    endAdornment={<EndAdornment>%</EndAdornment>}
                    selectOnFocus
                  />
                </Grid>
                <Grid item xs={4}>
                  <NumberInput
                    value={resultedPosition?.factor}
                    disabled={!editableCols.includes('factor')}
                    onBlur={(value) => {
                      onBlur({
                        id: 'factor',
                        value,
                      });
                    }}
                    onChange={(value) => {
                      onChange({
                        id: 'factor',
                        value,
                      });
                    }}
                    label={t('FIELDS.factor')}
                    decimals={2}
                    truncate
                    selectOnFocus
                    hideControls
                  />
                </Grid>
                <Grid item xs={4}>
                  <NumberInput
                    value={resultedPosition?.percent}
                    disabled={!editableCols.includes('percent')}
                    onBlur={(value) => {
                      onBlur({
                        id: 'percent',
                        value,
                      });
                    }}
                    onChange={(value) => {
                      onChange({
                        id: 'percent',
                        value,
                      });
                    }}
                    label={t('SERVICECONTRACT.MAINTENANCE_SHARE')}
                    decimals={2}
                    truncate
                    selectOnFocus
                    hideControls
                    unit="%"
                  />
                </Grid>
                <Grid item xs={4}>
                  <NumberInput
                    value={resultedPosition?.price}
                    disabled={!editableCols.includes('price')}
                    onBlur={(value) => {
                      onBlur({
                        id: 'price',
                        value,
                      });
                    }}
                    onChange={(value) => {
                      onChange({
                        id: 'price',
                        value,
                      });
                    }}
                    label={t('FIELDS.E_Price')}
                    decimals={2}
                    truncate
                    selectOnFocus
                    hideControls
                  />
                </Grid>
                <Grid item xs={4}>
                  <NumberInput
                    value={resultedPosition?.purchasePrice}
                    disabled={!editableCols.includes('purchasePrice')}
                    onBlur={(value) => {
                      onBlur({
                        id: 'purchasePrice',
                        value,
                      });
                    }}
                    onChange={(value) => {
                      onChange({
                        id: 'purchasePrice',
                        value,
                      });
                    }}
                    label={t('FIELDS.EK_Price')}
                    decimals={2}
                    truncate
                    selectOnFocus
                    hideControls
                  />
                </Grid>
                <Grid item xs={4}>
                  <NumberInput
                    value={resultedPosition?.servicePricePerYear}
                    disabled={!editableCols.includes('servicePricePerYear')}
                    onBlur={(value) => {
                      onBlur({
                        id: 'servicePricePerYear',
                        value,
                      });
                    }}
                    onChange={(value) => {
                      onChange({
                        id: 'servicePricePerYear',
                        value,
                      });
                    }}
                    label={t('SERVICECONTRACT.PRICE_TOTAL')}
                    decimals={2}
                    truncate
                    selectOnFocus
                    hideControls
                  />
                </Grid>
              </Grid>
            </Collapse>
          )}
        </div>
      </div>
    </LockOverride>
  );
}
